/**
 * Generate a 1080×1080 PNG share card with the user's reading.
 *
 * Implementation A: Native Canvas 2D.
 * If ligature rendering breaks on any target browser, swap to Implementation B
 * (html2canvas / modern-screenshot against a hidden DOM mirror) behind the same signature.
 */
const SIZE = 1080;

function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let current = '';
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

async function renderCanvas(pathCells, verse, grid, gridTa, lang) {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  // Saffron gradient background
  const bg = ctx.createLinearGradient(0, 0, 0, SIZE);
  bg.addColorStop(0, '#FFFBF0');
  bg.addColorStop(1, '#FFE8B8');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Decorative border
  ctx.strokeStyle = '#DAA520';
  ctx.lineWidth = 6;
  ctx.strokeRect(30, 30, SIZE - 60, SIZE - 60);

  // Title
  ctx.fillStyle = '#8B4513';
  ctx.textAlign = 'center';
  ctx.font = '600 48px "Noto Sans Tamil", "Playfair Display", serif';
  ctx.fillText("Sri Rama's Guidance", SIZE / 2, 120);

  // Collected letters strip
  const letters = pathCells.map(idx => grid[Math.floor(idx / 9)][idx % 9]);
  const lettersTa = pathCells.map(idx => gridTa[Math.floor(idx / 9)][idx % 9]);
  const stripY = 220;
  const stripGap = SIZE / 11;
  ctx.font = '600 72px "Noto Sans Devanagari", serif';
  ctx.fillStyle = '#5D3A1A';
  letters.forEach((ch, i) => {
    ctx.fillText(ch, stripGap * (i + 1.5), stripY);
  });
  ctx.font = '400 28px "Noto Sans Tamil", sans-serif';
  ctx.fillStyle = '#9B8B7A';
  lettersTa.forEach((ch, i) => {
    ctx.fillText(ch, stripGap * (i + 1.5), stripY + 52);
  });

  // Verse (Devanagari)
  ctx.textAlign = 'center';
  ctx.fillStyle = '#2C1810';
  ctx.font = '500 40px "Noto Sans Devanagari", serif';
  const devLines = wrapText(ctx, verse.devanagari, SIZE - 160);
  let y = 400;
  devLines.forEach(line => {
    ctx.fillText(line, SIZE / 2, y);
    y += 56;
  });

  // Transliteration (Tamil)
  ctx.font = '400 32px "Noto Sans Tamil", sans-serif';
  ctx.fillStyle = '#555';
  const taLines = wrapText(ctx, verse.transliteration_ta, SIZE - 160);
  y += 20;
  taLines.forEach(line => {
    ctx.fillText(line, SIZE / 2, y);
    y += 44;
  });

  // Meaning (primary)
  const meaning = lang === 'ta' ? verse.meaning_ta : verse.meaning_en;
  ctx.font = '400 30px "Noto Sans Tamil", sans-serif';
  ctx.fillStyle = '#2C1810';
  const meaningLines = wrapText(ctx, meaning, SIZE - 200);
  y += 40;
  meaningLines.forEach(line => {
    ctx.fillText(line, SIZE / 2, y);
    y += 42;
  });

  // Attribution (bottom)
  ctx.font = '400 22px "Noto Sans Tamil", sans-serif';
  ctx.fillStyle = '#8B6914';
  ctx.fillText('via JyotishSastra', SIZE / 2, SIZE - 60);

  const dataUrl = canvas.toDataURL('image/png');
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  return { dataUrl, blob };
}

export default async function shareImageRenderer(pathCells, verse, grid, gridTa, lang) {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // Continue without waiting — fallback fonts may be used
    }
  }
  return renderCanvas(pathCells, verse, grid, gridTa, lang);
}
